import { Component, OnInit } from '@angular/core';
import { AllLeadsListComponent } from './all-leads-list/all-leads-list.component';
import { Router } from '@angular/router';
import { FooterButton, FooterService, } from '../reusable-components/footer/footer.service';
import { LeadService } from '../services/leads/lead.service';
import { firstValueFrom, take } from 'rxjs';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectedLeadsPreviewComponent } from './selected-leads-preview/selected-leads-preview.component';
import { MapComponent } from './map/map.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { calculateDistanceInMeters, GeolocationService, } from '../services/geolocation/geolocation.service';
import { SettingsService } from '../services/indexed-db/object-stores/settings/settings.service';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-all-leads-view',
  imports: [
    AllLeadsListComponent,
    SelectedLeadsPreviewComponent,
    MapComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TranslatePipe,
    MatIcon,
  ],
  templateUrl: './all-leads-view.component.html',
  styleUrl: './all-leads-view.component.scss',
})
export class AllLeadsViewComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly footerService: FooterService,
    private readonly leadService: LeadService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly geolocationService: GeolocationService,
  ) {
    this.footerService.setRightButton(this.continueButton);
    this.footerService.clearLeftButton();
  }

  continueButton: FooterButton = {
    label: 'components.all-leads-view.continue',
    click: () => {
      this.continueButtonClickHandler();
    },
  };

  async checkForExistingExamination(): Promise<void> {
    const differentLeadError = 'lead from last Examination differs to this';
    const couldNotReserveError =
      'lead from last Examination cannot be reserved';
    try {
      //load last examination from indexeddb
      const leadWithExaminationResult =
        await this.settingsService.getLeadWithExaminationResult();
      const location = await this.geolocationService.getPosition();
      const distance = calculateDistanceInMeters(
        location.coords,
        leadWithExaminationResult.lead.coordinates,
      );
      //fetch all leads so lead of last examination should be contained
      //fetch all leads in 1.4 times the distance to last examination's lead, just to make sure the lead is contained in this fetch
      this.leadService.fetchLeads(
        location.coords.latitude,
        location.coords.longitude,
        (1.4 * distance) / 1000,
      );
      await firstValueFrom(this.leadService.allLeadsChanges$);
      if (
        JSON.stringify(
          this.leadService.getLead(leadWithExaminationResult.lead.id),
        ) !== JSON.stringify(leadWithExaminationResult.lead)
      ) {
        throw new Error(differentLeadError);
      }
      this.leadService.selectLead(leadWithExaminationResult.lead);
      const notReservableIds = await firstValueFrom(
        this.leadService.reserveSelectedLeads(),
      );
      if (notReservableIds.length > 0) {
        throw new Error(couldNotReserveError);
      }
      await this.router.navigate(['/lead-task']);
    } catch (e) {
      if (e instanceof Error) {
        const message = e.message;
        if (
          message === differentLeadError ||
          message === couldNotReserveError
        ) {
          this.snackbarService.show(
            'components.all-leads-view.cannot-continue-last-examination',
          );
        }
      }
      void this.settingsService.deleteLeadWithExaminationResult();
      this.leadService.initialFetch();
    }
  }

  continueButtonClickHandler() {
    if (this.leadService.hasSelectedLeads()) {
      this.leadService
        .reserveSelectedLeads()
        .pipe(take(1))
        .subscribe({
          next: () => {
            void this.router.navigate(['/lead-task']);
          },
          error: () => {
            this.snackbarService.show(
              this.translateService.instant(
                'components.all-leads-view.reservations-failed',
              ),
              3000,
            );
          },
        });
    }
  }

  ngOnInit(): void {
    void this.checkForExistingExamination();
  }
}
